import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { Indicator } from '../../../models/indicator';
import { GlobalsStore } from '../../../stores/globals.state';
import { RouterLink } from '@angular/router';
import { NgIf } from '@angular/common';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { TooltipModule } from 'primeng/tooltip';
import { CategoryStore } from '../../../stores/category/category.state';
import { ActiveUserStore } from '../../../stores/user/active-user.state';

export type TableRowAction =
  | 'select'
  | 'edit'
  | 'info'
  | 'delete'
  | 'addToAtlas'
  | 'openInAtlas'
  | 'addToSelection'
  | 'generateView';
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-table-row-indicator]',
  standalone: true,
  imports: [ButtonModule, RouterLink, NgIf, TooltipModule],
  templateUrl: './table-row-indicator.component.html',
  styleUrl: './table-row-indicator.component.scss',
})
export class TableRowIndicatorComponent {
  readonly globalStore = inject(GlobalsStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);

  @Input() item!: Indicator;
  @Input() selectedIcon?: string = '';
  @Input() global = true;
  @Input() actions: TableRowAction[] = [];
  @Input() fullName = false;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() addToWidgetsEvent = new EventEmitter<Indicator>();
  @Output() generateViewEvent = new EventEmitter<Indicator>();
  @Output() addToSelectionEvent = new EventEmitter<string>();

  /**
   * Selects the given indicator and updates the global store with the selected indicator if the global context is active.
   * Optionally, it closes the parent context after selection.
   *
   * @param {Indicator} indicator - The indicator to be selected.
   * @return {void} Does not return a value.
   */
  select(indicator: Indicator) {
    if (this.global) {
      this.globalStore.setSelectedIndicator(indicator);
      this.closeParent();
    }
  }

  /**
   * Retrieves the child indicators associated with a given parent indicator ID.
   *
   * @param {string} parentId - The unique identifier of the parent indicator.
   * @returns {Indicator[]} An array of child indicators associated with the specified parent ID.
   */
  getChildren = (parentId: string): Indicator[] => {
    return this.indicatorStore.filterByParent(parentId);
  };

  /**
   * Closes the parent component or performs the necessary cleanup action.
   * Emits a close event to notify listeners about the closure.
   *
   * @return {void} No return value.
   */
  closeParent(): void {
    this.closeEvent.emit();
  }

  /**
   * Generates a new view based on the provided indicator and emits an event.
   *
   * @param {Indicator} indicator - The indicator data used to generate the view.
   * @return {void} This method does not return a value.
   */
  generateView(indicator: Indicator): void {
    this.generateViewEvent.emit(indicator);
  }

  /**
   * Adds the provided indicator to the atlas and emits an event.
   *
   * @param {Indicator} indicator - The indicator to be added to the atlas.
   * @return {void} This method does not return a value.
   */
  addToAtlas(indicator: Indicator): void {
    this.addToWidgetsEvent.emit(indicator);
  }

  /**
   * Adds the specified `indicatorId` to the current selection and emits an event.
   *
   **/
  addToSelection(indicatorId: string): void {
    this.addToSelectionEvent.emit(indicatorId);
  }

  /**
   * Displays information about the given indicator.
   *
   * @param {Indicator} indicator - The indicator to be displayed.
   * @return {void} Does not return a value.
   */
  showInfo(indicator: Indicator): void {
    this.globalStore.setSelectedIndicator(indicator);
  }
}
